import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Chip,
  Tooltip,
  ButtonGroup,
  Stack,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ShoppingCart as OrderIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useDispatch } from "react-redux";
import { getMyntraFinanceDashboard } from "app/redux/slice/myntraSlice";
import { useSnackbar } from "notistack";
import Loader from "@jumbo/components/Loader";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const MyntraFinanceDashboard = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);

  const handleApplyFilter = () => {
    if (dateRange[0] === null || dateRange[1] === null) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }
    setLoading(true);
    const params = {
      fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
      toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
    };
    dispatch(getMyntraFinanceDashboard(params))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          setFinanceData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
      });
  };

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    setDateRange([null, null]);
    dispatch(getMyntraFinanceDashboard({}))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          setFinanceData(res?.data?.data);
        } else {
          enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Failed to fetch finance data", { variant: "error" });
      });
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const metrics = React.useMemo(() => {
    if (!financeData) return [];
    return [
      {
        key: "totalSale",
        title: "Total Sale",
        amount: financeData?.totalSale?.amount || 0,
        subtitle: `${financeData?.totalSale?.orderCount || 0} orders`,
        icon: <OrderIcon sx={{ color: theme.palette.primary.dark }} />,
        color: theme.palette.primary.main,
      },
      {
        key: "totalPlatformCost",
        title: "Total Commission and Tax",
        amount: financeData?.totalPlatformCost?.amount || 0,
        subtitle: `${financeData?.totalPlatformCost?.orderCount || 0} orders`,
        icon: <MoneyOffIcon sx={{ color: theme.palette.error.dark }} />,
        color: theme.palette.error.main,
      },
      {
        key: "overAllSettlement",
        title: "Final Amount",
        amount: financeData?.overAllSettlement?.amount || 0,
        subtitle: `${financeData?.overAllSettlement?.orderCount || 0} orders`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.info.dark }} />,
        color: theme.palette.info.main,
      },
      {
        key: "settledAmount",
        title: "Total Settled Amount",
        amount: financeData?.settledAmount?.amount || 0,
        subtitle: `${financeData?.settledAmount?.orderCount || 0} payments`,
        icon: <AccountBalanceIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "saleSettlement",
        title: "Sale Settled Amount",
        amount: financeData?.saleSettlement?.amount || 0,
        subtitle: `${financeData?.saleSettlement?.orderCount || 0} payments`,
        icon: <AccountBalanceIcon sx={{ color: theme.palette.success.dark }} />,
        color: theme.palette.success.main,
      },
      {
        key: "returnSettlement",
        title: "Return Amount",
        amount: financeData?.returnSettlement?.amount || 0,
        subtitle: `${financeData?.returnSettlement?.orderCount || 0} returns`,
        icon: (
          <AssignmentReturnIcon sx={{ color: theme.palette.warning.dark }} />
        ),
        color: theme.palette.warning.main,
      },
      {
        key: "pendingAmount",
        title: "Pending Amount",
        amount: financeData?.pendingAmount?.amount || 0,
        subtitle: `${financeData?.pendingAmount?.orderCount || 0} orders`,
        icon: <PendingActionsIcon sx={{ color: theme.palette.warning.dark }} />,
        color: theme.palette.warning.main,
      },
      {
        key: "totalCostPrice",
        title: "Total Cost Price",
        amount: financeData?.totalCostPrice?.amount || 0,
        subtitle: `${financeData?.totalCostPrice?.orderCount || 0} orders`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.info.dark }} />,
        color: theme.palette.info.dark,
      },
      {
        key: "deliveredCostPrice",
        title: "Delivered Cost Price",
        amount: financeData?.deliveredCostPrice?.amount || 0,
        subtitle: `${financeData?.deliveredCostPrice?.orderCount || 0} orders`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.info.dark }} />,
        color: theme.palette.info.dark,
      },
      {
        key: "totalMarketing",
        title: "Total Marketing",
        amount: financeData?.totalMarketing?.amount || 0,
        subtitle: `${financeData?.totalMarketing?.orderCount || 0} entries`,
        icon: <MonetizationOnIcon sx={{ color: theme.palette.info.dark }} />,
        color: theme.palette.info.dark,
      },
    ];
  }, [financeData, theme.palette]);

  const pl = financeData?.finalPL;
  const isProfit = pl?.status === "PROFIT";

  // Chart data preparation
  const chartData = React.useMemo(() => {
    if (!financeData?.monthlyData) return [];
    return financeData.monthlyData.map((item) => ({
      month: item.month,
      Sale: item.totalSale || 0,
      Cost: item.totalCostPrice || 0,
      Profit: item.finalPL || 0,
      // Profit: item.profit.amount || 0,
    }));
  }, [financeData]);

  const MetricCard = ({ metric }) => (
    <Card
      elevation={1}
      sx={{
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 4 },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(metric.color, 0.12),
            }}
          >
            {metric.icon}
          </Box>
          <Box>
            <Tooltip title={metric.title} placement="top-start">
              <Typography variant="subtitle2" color="text.secondary">
                {metric.title}
              </Typography>
            </Tooltip>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatCurrency(metric.amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {metric.subtitle}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" mb={2}>
        Myntra Finance Dashboard
      </Typography>

      {/* Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid item xs={12} sm={5}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Stack direction="row" spacing={1}>
                <ButtonGroup size="small" variant="outlined">
                  {QUICK_RANGES.map((r) => (
                    <Button
                      key={r.key}
                      onClick={() => setDateRange(r.getRange())}
                      disabled={loading}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                onClick={fetchDashboard}
                disabled={loading}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Loader />
      ) : !financeData ? (
        <Alert severity="info">No data available.</Alert>
      ) : (
        <Grid container spacing={3}>
          {/* P/L Banner */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: isProfit
                  ? `linear-gradient(135deg, ${theme.palette.success.light}, ${theme.palette.success.main})`
                  : `linear-gradient(135deg, ${alpha(
                      theme.palette.error.light,
                      0.6,
                    )}, ${alpha(theme.palette.error.main, 0.6)})`,
                color: theme.palette.common.white,
              }}
            >
              <CardContent>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    {isProfit ? (
                      <TrendingUpIcon fontSize="large" />
                    ) : (
                      <TrendingDownIcon fontSize="large" />
                    )}
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6">Total Profit / Loss</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {formatCurrency(pl?.amount || 0)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ mt: 1 }}
                    >
                      P/L = Sale Settled Amount − Return Amount - Delivered Cost
                      Price - Total Marketing
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      ={" "}
                      {formatCurrency(financeData?.saleSettlement?.amount || 0)}{" "}
                      −{" "}
                      {formatCurrency(
                        financeData?.returnSettlement?.amount || 0,
                      )}{" "}
                      −{" "}
                      {formatCurrency(
                        financeData?.deliveredCostPrice?.amount || 0,
                      )}{" "}
                      −{" "}
                      {formatCurrency(financeData?.totalMarketing?.amount || 0)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Chip
                      label={pl?.status || "N/A"}
                      color={isProfit ? "success" : "error"}
                      variant="filled"
                      sx={{ color: theme.palette.common.white, border: 1 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Metric Cards */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Overview</Typography>
              <Divider sx={{ flex: 1 }} />
            </Stack>
            <Grid container spacing={3}>
              {metrics.map((metric) => (
                <Grid key={metric.key} item xs={12} sm={6} md={4} lg={4}>
                  <MetricCard metric={metric} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Charts */}
          {chartData.length > 0 && (
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Sales & Cost Chart</Typography>
                <Divider sx={{ flex: 1 }} />
              </Stack>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Monthly Sales vs Cost
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Legend />
                          <Bar
                            dataKey="Sale"
                            fill={theme.palette.primary.main}
                          />
                          <Bar dataKey="Cost" fill={theme.palette.error.main} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Monthly Profit / Loss Trend
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <RechartsTooltip
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="Profit"
                            stroke={theme.palette.success.main}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default MyntraFinanceDashboard;
